import { useEffect, useState } from "react";

declare global {
  interface Window {
    googleMapsLoading: Promise<void> | null;
    google: typeof google;
  }
}

let googleMapsLoading: Promise<void> | null = null;

export const useGoogleMaps = () => {
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [scriptError, setScriptError] = useState<string | null>(null);

  useEffect(() => {
    if (window.google?.maps?.places) {
      setIsScriptLoaded(true);
      return;
    }

    if (googleMapsLoading) {
      googleMapsLoading
        .then(() => setIsScriptLoaded(true))
        .catch((error: unknown) =>
          setScriptError(error instanceof Error ? error.message : String(error))
        );
      return;
    }

    googleMapsLoading = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        if (window.google && window.google.maps && window.google.maps.places) {
          resolve();
        } else {
          reject(
            new Error("Google Maps API loaded but services not available")
          );
        }
      };

      script.onerror = () => {
        reject(new Error("Failed to load Google Maps API"));
      };

      document.head.appendChild(script);
    });

    googleMapsLoading
      .then(() => {
        setIsScriptLoaded(true);
        setScriptError(null);
      })
      .catch((error: unknown) => {
        setScriptError(error instanceof Error ? error.message : String(error));
        console.error("Google Maps API failed to load:", error);
        googleMapsLoading = null;
      });
  }, []);

  return { isScriptLoaded, scriptError };
};
